import { useQuery } from '@apollo/client';
import { miniButtonDockScrollBottomPad, translateTemplateText } from '@focuslee/shared-mini-app';
import { Box } from '@mui/material';
import { hapticFeedback } from '@tma.js/sdk';
import { useCallback, useEffect, useMemo, useState, type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { GetTemplateQuestsQuery, GetTemplatesQuery } from '../client/generated/graphql';
import { getTemplatesQuery } from '../client/queries/template';
import { getTemplateQuestsQuery } from '../client/queries/templateQuests';
import { Page } from '../components/Page';
import { CommonIcon } from '../components/common/CommonIcon';
import type { SegmentedChipItem } from '../components/common/SegmentedChipBar';
import { TemplateEmptyState } from '../components/templates/TemplateEmptyState';
import { TemplateList } from '../components/templates/TemplateList';
import { TemplatesPageDock } from '../components/templates/TemplatesPageDock';
import { TemplatesPageSkeleton } from '../components/templates/skeleton/TemplatesPageSkeleton';
import { getColor } from '../helpers/color';
import { questTemplateConfigPath } from '../navigation/paths';

export const TemplatesPage: FC = () => {
  const { t } = useTranslation();
  const { loading: loadingTemplates, data } = useQuery<GetTemplatesQuery>(getTemplatesQuery);
  const { loading: loadingQuests, data: questsData } = useQuery<GetTemplateQuestsQuery>(getTemplateQuestsQuery, { fetchPolicy: 'cache-first' });
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const selectedDate = searchParams.get('selectedDate');
  const preselectedTemplateId = searchParams.get('templateId');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const quests = questsData?.quests ?? [];
  const loading = loadingTemplates || loadingQuests;

  const templateCategories = useMemo(
    () => data?.templateCategories.filter(category => !category.system) ?? [],
    [data]
  );

  const filteredTemplateCategories = useMemo(() => {
    if (preselectedTemplateId) {
      const preselectedCategory = templateCategories.find(cat => cat.id === preselectedTemplateId);
      return preselectedCategory ? [preselectedCategory] : [];
    }
    return templateCategories;
  }, [templateCategories, preselectedTemplateId]);
  
  // Get set of used template IDs from quests
  const usedTemplateIds = useMemo(() => {
    return new Set(quests.filter(q => q.templateId).map(q => q.templateId!));
  }, [quests]);
  
  // Create category filter options (only show when not preselected)
  const categoryChips: SegmentedChipItem[] = useMemo(
    () =>
      preselectedTemplateId
        ? []
        : [
            {
              id: 'all',
              label: t('templates.all'),
              icon: <CommonIcon icon="apps" type="filled" size={14} color="var(--tg-theme-hint-color)" />,
            },
            ...filteredTemplateCategories.map(category => {
              const categoryData = category;
            return {
              id: category.id,
              label: translateTemplateText(t, category.id, 'name', category.name),
              icon: categoryData ? (
                <CommonIcon icon={categoryData.icon} type="filled" size={14} color={getColor(categoryData.iconColor)} />
              ) : (
                <CommonIcon icon="apps" type="filled" size={14} color="var(--tg-theme-hint-color)" />
              ),
            };
            }),
          ],
    [filteredTemplateCategories, preselectedTemplateId, t]
  );
  const scrollBottomPadding = `calc(${miniButtonDockScrollBottomPad} + ${categoryChips.length > 1 ? 46 : 0}px + ${searchOpen ? 72 : 0}px)`;

  // Filter templates based on search, category, and exclude already used templates
  const filteredCategories = filteredTemplateCategories
    .map(category => ({
      ...category,
      quests: category.quests.filter(quest => 
        !usedTemplateIds.has(quest.id) && // Exclude used templates
        (translateTemplateText(t, quest.id, 'name', quest.name).toLowerCase().includes(searchQuery.toLowerCase()) ||
        translateTemplateText(t, quest.id, 'description', quest.description ?? '').toLowerCase().includes(searchQuery.toLowerCase()))
      )
    }))
    .filter(category => 
      selectedCategory === 'all' || category.id === selectedCategory
    )
    .filter(category => category.quests.length > 0);

  useEffect(() => {
    if (preselectedTemplateId && filteredTemplateCategories.length > 0) {
      const category = filteredTemplateCategories.find(cat => cat.id === preselectedTemplateId);
      if (category)
        setSelectedCategory(category.id);
    }
  }, [preselectedTemplateId, filteredTemplateCategories]);


  const handleCategorySelect = (category: string) => {
    setSelectedCategory(category);
    hapticFeedback.impactOccurred.ifAvailable('light');
  };

  const handleTemplateSelect = (templateId: string) => {
    hapticFeedback.impactOccurred.ifAvailable('light');
    setSelectedTemplateId(selectedTemplateId === templateId ? null : templateId);
  };

  const handleSelectTemplate = useCallback(() => {
    if (!selectedTemplateId) return;
    
    const selectedTemplate = filteredTemplateCategories
      .flatMap(cat => cat.quests.map(quest => ({ ...quest, categoryId: cat.id })))
      .find(template => template.id === selectedTemplateId);
    
    if (selectedTemplate) {
      const path = questTemplateConfigPath.replace(':templateId', selectedTemplate.id);
      const searchParamsString = new URLSearchParams({
        ...(selectedDate && { selectedDate })
      }).toString();
      navigate(`${path}${searchParamsString ? `?${searchParamsString}` : ''}`);
    }
  }, [selectedTemplateId, filteredTemplateCategories, selectedDate]);

  const navigateBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  if (loading) return <TemplatesPageSkeleton />;

  return (
    <Page
      back
      insetEnabled={false}
      edgeToEdge
      footer={
        <TemplatesPageDock
          onBack={navigateBack}
          onSelect={handleSelectTemplate}
          selectEnabled={!!selectedTemplateId}
          searchOpen={searchOpen}
          searchQuery={searchQuery}
          onSearchQueryChange={setSearchQuery}
          onOpenSearch={() => setSearchOpen(true)}
          onCloseSearch={() => {
            setSearchOpen(false);
            setSearchQuery('');
          }}
          categoryChips={categoryChips}
          activeCategoryId={selectedCategory}
          onCategoryChange={handleCategorySelect}
        />
      }
    >
      <Box
        sx={{
          height: '100%',
          minHeight: 0,
          position: 'relative',
          overflow: 'hidden',
          bgcolor: 'var(--tg-theme-secondary-bg-color)',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            overflowY: 'auto',
            overflowX: 'hidden',
            WebkitOverflowScrolling: 'touch',
            paddingTop: 'calc(var(--tg-viewport-content-safe-area-inset-top, 0px) + var(--tg-viewport-safe-area-inset-top, 0px))',
            paddingBottom: scrollBottomPadding,
          }}
        >
          {filteredCategories.length > 0 ? (
            <TemplateList
              categories={filteredCategories}
              selectedCategory={selectedCategory}
              selectedTemplateId={selectedTemplateId}
              preselectedTemplateId={preselectedTemplateId}
              onTemplateSelect={handleTemplateSelect}
            />
          ) : (
            <TemplateEmptyState hasSearchQuery={!!searchQuery} />
          )}
        </Box>
      </Box>
    </Page>
  );
};
